"use client";

import React, { useState, useRef, useEffect, KeyboardEvent } from "react";
import { useDraggableBubble } from "./use-draggable-bubble";
import { AgentChatBubble } from "./agent-chat-bubble";
import { AgentChatActionButtons } from "./agent-chat-action-buttons";
import { AgentChatVoiceButton } from "./agent-chat-voice-button";

export interface AgentChatInputProps {
    onSendMessage: (message: string) => void;
    isLoading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    defaultCollapsed?: boolean;
    isActive?: boolean;
    isConnecting?: boolean;
    isThinking?: boolean;
    isPaused?: boolean;
    onToggleVoice?: () => void;
    onExpandedChange?: (isExpanded: boolean) => void;
}

export const AgentChatInput: React.FC<AgentChatInputProps> = ({
    onSendMessage,
    isLoading = false,
    disabled = false,
    placeholder = "Hỏi trợ lý AI hoặc yêu cầu chuyển cảnh...",
    className = "",
    defaultCollapsed = true,
    isActive = false,
    isConnecting = false,
    isThinking = false,
    isPaused = false,
    onToggleVoice,
    onExpandedChange,
}) => {
    const [text, setText] = useState("");
    const [isCollapsed, setIsCollapsed] = useState<boolean>(defaultCollapsed);
    const [isMultiLine, setIsMultiLine] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        onExpandedChange?.(!isCollapsed);
    }, [isCollapsed, onExpandedChange]);

    const handleCollapse = () => {
        setIsCollapsed(true);
    };

    const [keyboardOffset, setKeyboardOffset] = useState(0);

    // Dynamic keyboard tracking for iOS (Safari, Firefox iOS) and Android virtual keyboards
    useEffect(() => {
        if (typeof window === "undefined" || !window.visualViewport) return;

        const updateViewport = () => {
            const vv = window.visualViewport;
            if (!vv) return;
            const offset = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
            if (offset > 100) {
                setKeyboardOffset(offset);
            } else {
                setKeyboardOffset(0);
            }
        };

        const vv = window.visualViewport;
        vv.addEventListener("resize", updateViewport);
        vv.addEventListener("scroll", updateViewport);
        window.addEventListener("resize", updateViewport);

        return () => {
            vv.removeEventListener("resize", updateViewport);
            vv.removeEventListener("scroll", updateViewport);
            window.removeEventListener("resize", updateViewport);
        };
    }, []);

    const { bubblePos, isDragging, isSnapping, handleBubblePointerDown } = useDraggableBubble({
        onTap: () => {
            setIsCollapsed(false);
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 100);
        },
    });

    // Auto-adjust textarea height dynamically and adapt layout between 1-row pill and 2-row card
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        if (!text) {
            textarea.style.height = "24px";
            setIsMultiLine(false);
            return;
        }

        textarea.style.height = "auto";
        const scrollHeight = textarea.scrollHeight;
        const multiline = text.includes("\n") || scrollHeight > 32;
        setIsMultiLine(multiline);

        if (multiline) {
            const maxHeight = 88; // ~3-4 lines max with comfortable line-height
            textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        } else {
            textarea.style.height = "24px";
        }
    }, [text]);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value);
    };

    const handleSubmit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const trimmed = text.trim();
        if (!trimmed || isLoading || disabled) return;

        onSendMessage(trimmed);
        setText("");
        setIsMultiLine(false);

        if (textareaRef.current) {
            textareaRef.current.style.height = "24px";
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        e.stopPropagation();
        if (e.nativeEvent.isComposing) return;

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        } else if (e.key === "Escape") {
            handleCollapse();
        }
    };

    const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

    // Mode 1: Draggable Floating AI Bubble
    if (isCollapsed) {
        return (
            <AgentChatBubble
                bubblePos={bubblePos}
                isDragging={isDragging}
                isSnapping={isSnapping}
                isLoading={isLoading}
                isListening={isActive}
                onPointerDown={handleBubblePointerDown}
                className={className}
            />
        );
    }

    const voiceButton = onToggleVoice ? (
        <AgentChatVoiceButton
            isActive={isActive}
            isConnecting={isConnecting}
            isThinking={isThinking || isLoading}
            isPaused={isPaused}
            onToggleVoice={onToggleVoice}
        />
    ) : null;

    // Mode 2: Unified Glassmorphism Dock (Active Text Input Mode)
    return (
        <div
            data-agent-chat-input="true"
            onMouseDown={stopPropagation}
            onTouchStart={stopPropagation}
            onClick={stopPropagation}
            onPointerDown={stopPropagation}
            style={{ bottom: keyboardOffset > 0 ? `${keyboardOffset + 12}px` : undefined }}
            className={`fixed ${keyboardOffset > 0 ? '' : 'bottom-[140px] sm:bottom-[150px] md:bottom-[155px]'} right-3 sm:right-6 md:right-8 z-50 w-[calc(100%-1.5rem)] sm:w-[380px] md:w-[410px] max-w-[420px] pointer-events-auto transition-all duration-200 animate-in fade-in zoom-in-95 ${className}`}
        >
            <form
                onSubmit={handleSubmit}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
                onClick={stopPropagation}
                className={`relative bg-[#0c121e]/90 hover:bg-[#0c121e]/95 border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.85)] hover:border-white/30 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/25 transition-all duration-200 ${
                    isMultiLine
                        ? "flex flex-col gap-2 p-2.5 sm:p-3 rounded-2xl"
                        : "flex flex-row items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-full"
                }`}
            >
                {!isMultiLine && (
                    <>
                        {voiceButton}
                        {voiceButton && <div className="h-5 w-[1px] bg-white/15 mx-0.5 shrink-0 hidden sm:block" />}
                    </>
                )}

                <textarea
                    ref={textareaRef}
                    rows={isMultiLine ? 2 : 1}
                    value={text}
                    onChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isLoading}
                    placeholder={placeholder}
                    className={`bg-transparent text-xs sm:text-sm text-white font-medium placeholder:text-slate-400 focus:outline-none disabled:opacity-50 resize-none select-text ${
                        isMultiLine
                            ? "w-full px-1 py-0.5 overflow-y-auto leading-relaxed scrollbar-thin scrollbar-thumb-white/25 scrollbar-track-transparent"
                            : "flex-1 px-1.5 py-0 min-w-0 leading-6 placeholder:truncate overflow-hidden"
                    }`}
                    style={{
                        height: isMultiLine ? undefined : "24px",
                        minHeight: isMultiLine ? "48px" : "24px",
                        maxHeight: "88px",
                    }}
                />

                <AgentChatActionButtons
                    isMultiLine={isMultiLine}
                    hasText={Boolean(text.trim())}
                    isLoading={isLoading}
                    disabled={disabled}
                    voiceButton={voiceButton}
                    onCollapse={handleCollapse}
                />
            </form>
        </div>
    );
};

export default AgentChatInput;
